const STORAGE_KEY = 'fps_daily_challenges';
const WEEKLY_STORAGE_KEY = 'fps_weekly_challenges_v1';

export type ChallengeId = 'eliminate_3' | 'open_2_chests' | 'reach_phase_3';

export type WeeklyChallengeId =
  | 'weekly_elim_5'
  | 'weekly_chest_4'
  | 'weekly_phase_4'
  | 'weekly_damage_500'
  | 'weekly_win_1'
  | 'weekly_outlast_15';

type ChallengeDef = {
  id: ChallengeId;
  title: string;
  target: number;
  xp: number;
};

type PersistedState = {
  date: string;
  progress: Record<ChallengeId, number>;
  completed: Record<ChallengeId, boolean>;
};

const CHALLENGES: ChallengeDef[] = [
  { id: 'eliminate_3', title: 'Eliminate 3 opponents', target: 3, xp: 55 },
  { id: 'open_2_chests', title: 'Open 2 chests', target: 2, xp: 40 },
  { id: 'reach_phase_3', title: 'Reach storm phase 3', target: 3, xp: 45 },
];

function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoWeekKey(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const week = 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

function defaultState(): PersistedState {
  return {
    date: todayKey(),
    progress: {
      eliminate_3: 0,
      open_2_chests: 0,
      reach_phase_3: 0,
    },
    completed: {
      eliminate_3: false,
      open_2_chests: false,
      reach_phase_3: false,
    },
  };
}

function loadState(): PersistedState {
  const today = todayKey();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as PersistedState;
    if (parsed.date !== today) return defaultState();
    const base = defaultState();
    for (const c of CHALLENGES) {
      const p = parsed.progress?.[c.id];
      base.progress[c.id] =
        Number.isFinite(p) && p >= 0 ? Math.min(c.target, Math.floor(p)) : 0;
      base.completed[c.id] = Boolean(parsed.completed?.[c.id]);
      if (base.completed[c.id]) {
        base.progress[c.id] = c.target;
      }
    }
    return base;
  } catch {
    return defaultState();
  }
}

function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export type ChallengeCompleteEvent = { id: ChallengeId; title: string; xp: number };

type WeeklyChallengeDef = {
  id: WeeklyChallengeId;
  title: string;
  target: number;
  xp: number;
};

const WEEKLY_POOL: WeeklyChallengeDef[] = [
  { id: 'weekly_elim_5', title: 'Eliminate 5 opponents', target: 5, xp: 120 },
  { id: 'weekly_chest_4', title: 'Open 4 chests', target: 4, xp: 90 },
  { id: 'weekly_phase_4', title: 'Reach storm phase 4', target: 4, xp: 100 },
  { id: 'weekly_damage_500', title: 'Deal 500 damage', target: 500, xp: 110 },
  { id: 'weekly_win_1', title: 'Win a match', target: 1, xp: 150 },
  { id: 'weekly_outlast_15', title: 'Outlast 15 players', target: 15, xp: 95 },
];

type WeeklyPersistedState = {
  week: string;
  progress: Record<WeeklyChallengeId, number>;
  completed: Record<WeeklyChallengeId, boolean>;
};

function weeklyDefaultState(ids: WeeklyChallengeId[]): WeeklyPersistedState {
  const progress = {} as Record<WeeklyChallengeId, number>;
  const completed = {} as Record<WeeklyChallengeId, boolean>;
  for (const id of ids) {
    progress[id] = 0;
    completed[id] = false;
  }
  return { week: isoWeekKey(), progress, completed };
}

function getWeeklyChallengesForWeek(): WeeklyChallengeDef[] {
  const weekNum = parseInt(isoWeekKey().split('-W')[1] ?? '1', 10);
  const out: WeeklyChallengeDef[] = [];
  for (let i = 0; i < 3; i++) {
    out.push(WEEKLY_POOL[(weekNum + i * 2) % WEEKLY_POOL.length]!);
  }
  return out;
}

function loadWeeklyState(): { defs: WeeklyChallengeDef[]; state: WeeklyPersistedState } {
  const defs = getWeeklyChallengesForWeek();
  const week = isoWeekKey();
  const ids = defs.map((d) => d.id);
  try {
    const raw = localStorage.getItem(WEEKLY_STORAGE_KEY);
    if (!raw) return { defs, state: weeklyDefaultState(ids) };
    const parsed = JSON.parse(raw) as WeeklyPersistedState;
    if (parsed.week !== week) return { defs, state: weeklyDefaultState(ids) };
    const base = weeklyDefaultState(ids);
    for (const c of defs) {
      const p = parsed.progress?.[c.id];
      base.progress[c.id] =
        Number.isFinite(p) && p >= 0 ? Math.min(c.target, Math.floor(p)) : 0;
      base.completed[c.id] = Boolean(parsed.completed?.[c.id]);
      if (base.completed[c.id]) base.progress[c.id] = c.target;
    }
    return { defs, state: base };
  } catch {
    return { defs, state: weeklyDefaultState(ids) };
  }
}

function saveWeeklyState(state: WeeklyPersistedState): void {
  try {
    localStorage.setItem(WEEKLY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export type WeeklyChallengeCompleteEvent = { id: WeeklyChallengeId; title: string; xp: number };

/** Top-left daily quest panel with localStorage persistence for the current day. */
export class FpsDailyChallenges {
  /** Compact lobby HTML — three daily quests with progress bars. */
  static getLobbySummary(): string {
    const state = loadState();
    const rows = CHALLENGES.map((c) => {
      const done = state.completed[c.id];
      const cur = state.progress[c.id];
      const pct = done ? 100 : Math.round((Math.min(cur, c.target) / c.target) * 100);
      const barColor = done ? '#7dffb0' : '#2ee6ff';
      const labelColor = done ? '#9dffc0' : '#b8dce8';
      const progText = done ? 'Done' : `${Math.min(cur, c.target)}/${c.target}`;
      return (
        '<div style="margin-bottom:7px">' +
        '<div style="display:flex;align-items:center;justify-content:space-between;gap:6px;margin-bottom:3px">' +
        `<span style="font:600 10px system-ui;color:${labelColor};line-height:1.25">${c.title}</span>` +
        `<span style="font:700 9px system-ui;color:${done ? '#7dffb0' : '#8ec8dd'};letter-spacing:0.04em;white-space:nowrap">${progText}</span>` +
        '</div>' +
        '<div style="height:5px;border-radius:3px;background:rgba(255,255,255,0.1);overflow:hidden">' +
        `<div style="height:100%;width:${pct}%;background:${barColor};transition:width 0.2s ease-out"></div>` +
        '</div>' +
        '</div>'
      );
    }).join('');
    return (
      '<div id="lobby-daily-challenges" style="margin-top:10px;padding:10px 12px;border-radius:10px;' +
      'background:rgba(0,0,0,0.28);border:1px solid rgba(46,230,255,0.35);text-align:left">' +
      '<div style="font:800 10px system-ui;color:#2ee6ff;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:8px">' +
      'Daily Challenges</div>' +
      rows +
      '</div>'
    );
  }

  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly headerLabel: HTMLSpanElement;
  private readonly chevron: HTMLSpanElement;
  private readonly rowEls = new Map<ChallengeId, { row: HTMLDivElement; prog: HTMLDivElement }>();
  private readonly weeklyRowEls = new Map<WeeklyChallengeId, { row: HTMLDivElement; prog: HTMLDivElement }>();
  private readonly state: PersistedState;
  private readonly weeklyDefs: WeeklyChallengeDef[];
  private readonly weeklyState: WeeklyPersistedState;
  private collapsed = false;
  private disposed = false;
  private readonly onComplete?: (ev: ChallengeCompleteEvent) => void;
  private readonly onWeeklyComplete?: (ev: WeeklyChallengeCompleteEvent) => void;

  constructor(
    mount: HTMLElement,
    onComplete?: (ev: ChallengeCompleteEvent) => void,
    onWeeklyComplete?: (ev: WeeklyChallengeCompleteEvent) => void,
  ) {
    this.onComplete = onComplete;
    this.onWeeklyComplete = onWeeklyComplete;
    this.state = loadState();
    const weekly = loadWeeklyState();
    this.weeklyDefs = weekly.defs;
    this.weeklyState = weekly.state;

    this.root = document.createElement('div');
    this.root.className = 'fps-daily-challenges-panel';
    this.root.style.cssText = [
      'position:fixed',
      'top:52px',
      'left:14px',
      'z-index:40',
      'width:min(240px,42vw)',
      'pointer-events:auto',
      'box-sizing:border-box',
    ].join(';');

    const header = document.createElement('button');
    header.type = 'button';
    header.style.cssText = [
      'width:100%',
      'display:flex',
      'align-items:center',
      'justify-content:space-between',
      'gap:8px',
      'padding:7px 10px',
      'border-radius:8px 8px 0 0',
      'border:2px solid #2ee6ff',
      'border-bottom:none',
      'background:rgba(12,16,28,0.94)',
      'color:#e8f8ff',
      'font:800 10px system-ui,sans-serif',
      'letter-spacing:0.1em',
      'cursor:pointer',
      'box-shadow:0 4px 14px rgba(0,0,0,0.35)',
    ].join(';');

    this.headerLabel = document.createElement('span');
    this.headerLabel.textContent = 'DAILY CHALLENGES';

    this.chevron = document.createElement('span');
    this.chevron.textContent = '▾';
    this.chevron.style.cssText = 'font-size:12px;color:#2ee6ff;transition:transform 0.18s ease';

    header.appendChild(this.headerLabel);
    header.appendChild(this.chevron);
    header.addEventListener('click', () => {
      this.setCompact(!this.collapsed);
    });

    this.body = document.createElement('div');
    this.body.style.cssText = [
      'padding:8px 10px 10px',
      'border-radius:0 0 8px 8px',
      'background:rgba(10,14,24,0.92)',
      'border:2px solid #2ee6ff',
      'border-top:1px solid rgba(46,230,255,0.35)',
      'box-shadow:0 6px 18px rgba(0,0,0,0.4)',
    ].join(';');

    for (const c of CHALLENGES) {
      this.body.appendChild(this.buildChallengeRow(c.id, c.title, this.rowEls));
    }

    const weeklyHeader = document.createElement('div');
    weeklyHeader.style.cssText =
      'margin:10px 0 6px;padding-top:8px;border-top:1px solid rgba(196,181,253,0.35);font:800 9px system-ui;color:#c4b5fd;letter-spacing:0.1em';
    weeklyHeader.textContent = `WEEKLY · ${isoWeekKey()}`;
    this.body.appendChild(weeklyHeader);

    for (const c of this.weeklyDefs) {
      this.body.appendChild(this.buildChallengeRow(c.id, c.title, this.weeklyRowEls, '#c4b5fd'));
    }

    this.root.appendChild(header);
    this.root.appendChild(this.body);
    mount.appendChild(this.root);
    this.renderAll();
    this.renderAllWeekly();
    this.setCompact(window.matchMedia?.('(max-width: 720px), (max-height: 520px)')?.matches ?? false);
  }

  /** Compact lobby HTML — three weekly quests for the current ISO week. */
  static getWeeklyLobbySummary(): string {
    const { defs, state } = loadWeeklyState();
    const rows = defs.map((c) => {
      const done = state.completed[c.id];
      const cur = state.progress[c.id];
      const pct = done ? 100 : Math.round((Math.min(cur, c.target) / c.target) * 100);
      const barColor = done ? '#b8a0ff' : '#9b7bff';
      const labelColor = done ? '#d4c4ff' : '#c8b8f0';
      const progText = done ? 'Done' : `${Math.min(cur, c.target)}/${c.target}`;
      return (
        '<div style="margin-bottom:7px">' +
        '<div style="display:flex;align-items:center;justify-content:space-between;gap:6px;margin-bottom:3px">' +
        `<span style="font:600 10px system-ui;color:${labelColor};line-height:1.25">${c.title}</span>` +
        `<span style="font:700 9px system-ui;color:${done ? '#b8a0ff' : '#a898e8'};white-space:nowrap">${progText}</span>` +
        '</div>' +
        '<div style="height:5px;border-radius:3px;background:rgba(255,255,255,0.1);overflow:hidden">' +
        `<div style="height:100%;width:${pct}%;background:${barColor};transition:width 0.2s ease-out"></div>` +
        '</div>' +
        '</div>'
      );
    }).join('');
    return (
      '<div style="margin-top:10px;padding:10px 12px;border-radius:10px;' +
      'background:rgba(0,0,0,0.28);border:1px solid rgba(155,123,255,0.4);text-align:left">' +
      `<div style="font:800 10px system-ui;color:#9b7bff;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:8px">` +
      `Weekly Challenges · ${isoWeekKey()}</div>` +
      rows +
      '</div>'
    );
  }

  recordElimination(): void {
    this.bump('eliminate_3', 1);
    this.bumpWeekly('weekly_elim_5', 1);
  }

  recordChestOpen(): void {
    this.bump('open_2_chests', 1);
    this.bumpWeekly('weekly_chest_4', 1);
  }

  recordStormPhase(phase: number): void {
    if (!Number.isFinite(phase)) return;
    this.setProgressAtLeast('reach_phase_3', Math.floor(phase));
    this.setWeeklyProgressAtLeast('weekly_phase_4', Math.floor(phase));
  }

  recordDamage(amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.bumpWeekly('weekly_damage_500', Math.floor(amount));
  }

  recordMatchWin(): void {
    this.bumpWeekly('weekly_win_1', 1);
  }

  recordOutlast(count: number): void {
    if (!Number.isFinite(count) || count <= 0) return;
    this.setWeeklyProgressAtLeast('weekly_outlast_15', Math.floor(count));
  }

  update(_dt: number): void {
    /* reserved for future toast animations */
  }

  setCompact(compact: boolean): void {
    if (this.disposed || this.collapsed === compact) return;
    this.collapsed = compact;
    this.root.dataset.collapsed = compact ? 'true' : 'false';
    this.body.style.display = compact ? 'none' : 'block';
    this.chevron.style.transform = compact ? 'rotate(-90deg)' : 'rotate(0deg)';
    this.headerLabel.textContent = compact ? this.getCompactLabel() : 'DAILY CHALLENGES';
  }

  setVisible(visible: boolean): void {
    if (this.disposed) return;
    this.root.style.display = visible ? 'block' : 'none';
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.root.remove();
  }

  private bump(id: ChallengeId, delta: number): void {
    if (this.disposed || this.state.completed[id]) return;
    const def = CHALLENGES.find((c) => c.id === id);
    if (!def) return;
    this.setProgressAtLeast(id, this.state.progress[id] + delta);
  }

  private setProgressAtLeast(id: ChallengeId, value: number): void {
    if (this.disposed || this.state.completed[id]) return;
    const def = CHALLENGES.find((c) => c.id === id);
    if (!def) return;

    const next = Math.max(this.state.progress[id], Math.min(def.target, Math.floor(value)));
    if (next === this.state.progress[id]) return;
    this.state.progress[id] = next;
    if (next >= def.target) {
      this.complete(id, def);
    } else {
      saveState(this.state);
      this.renderRow(id, def);
    }
  }

  private complete(id: ChallengeId, def: ChallengeDef): void {
    if (this.state.completed[id]) return;
    this.state.completed[id] = true;
    this.state.progress[id] = def.target;
    saveState(this.state);
    this.renderRow(id, def);
    this.onComplete?.({ id, title: def.title, xp: def.xp });
  }

  private renderAll(): void {
    for (const c of CHALLENGES) {
      this.renderRow(c.id, c);
    }
    if (this.collapsed) this.headerLabel.textContent = this.getCompactLabel();
  }

  private buildChallengeRow(
    id: ChallengeId | WeeklyChallengeId,
    title: string,
    map: Map<ChallengeId | WeeklyChallengeId, { row: HTMLDivElement; prog: HTMLDivElement }>,
    accent = '#2ee6ff',
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText =
      'display:flex;align-items:flex-start;gap:8px;margin-bottom:7px;font:600 11px system-ui,sans-serif;color:#d8ecff;line-height:1.35';

    const check = document.createElement('span');
    check.style.cssText =
      `flex:0 0 14px;width:14px;height:14px;border-radius:4px;border:1px solid ${accent}88;` +
      'display:inline-flex;align-items:center;justify-content:center;font-size:10px;margin-top:1px';

    const textWrap = document.createElement('div');
    textWrap.style.cssText = 'flex:1;min-width:0';

    const titleEl = document.createElement('div');
    titleEl.textContent = title;

    const prog = document.createElement('div');
    prog.style.cssText = 'margin-top:2px;font:700 10px system-ui;color:#8ec8dd;letter-spacing:0.04em';

    textWrap.appendChild(titleEl);
    textWrap.appendChild(prog);
    row.appendChild(check);
    row.appendChild(textWrap);
    map.set(id, { row, prog });
    return row;
  }

  private getCompactLabel(): string {
    let done = 0;
    for (const c of CHALLENGES) {
      if (this.state.completed[c.id]) done += 1;
    }
    let weeklyDone = 0;
    for (const c of this.weeklyDefs) {
      if (this.weeklyState.completed[c.id]) weeklyDone += 1;
    }
    return `DAILY ${done}/${CHALLENGES.length} · WK ${weeklyDone}/${this.weeklyDefs.length}`;
  }

  private renderRow(id: ChallengeId, def: ChallengeDef): void {
    const els = this.rowEls.get(id);
    if (!els) return;
    const done = this.state.completed[id];
    const cur = this.state.progress[id];
    els.prog.textContent = done ? 'Complete' : `${Math.min(cur, def.target)} / ${def.target}`;
    els.prog.style.color = done ? '#7dffb0' : '#8ec8dd';
    els.row.style.opacity = done ? '0.78' : '1';

    const check = els.row.firstElementChild as HTMLSpanElement;
    if (done) {
      check.textContent = '✓';
      check.style.background = 'rgba(80,220,140,0.25)';
      check.style.borderColor = 'rgba(120,255,170,0.75)';
      check.style.color = '#9dffc0';
    } else {
      check.textContent = '';
      check.style.background = 'transparent';
      check.style.borderColor = 'rgba(46,230,255,0.55)';
      check.style.color = 'transparent';
    }
    if (this.collapsed) this.headerLabel.textContent = this.getCompactLabel();
  }

  private bumpWeekly(id: WeeklyChallengeId, delta: number): void {
    if (this.disposed || this.weeklyState.completed[id]) return;
    const def = this.weeklyDefs.find((c) => c.id === id);
    if (!def) return;
    this.setWeeklyProgressAtLeast(id, this.weeklyState.progress[id] + delta);
  }

  private setWeeklyProgressAtLeast(id: WeeklyChallengeId, value: number): void {
    if (this.disposed || this.weeklyState.completed[id]) return;
    const def = this.weeklyDefs.find((c) => c.id === id);
    if (!def) return;

    const next = Math.max(this.weeklyState.progress[id], Math.min(def.target, Math.floor(value)));
    if (next === this.weeklyState.progress[id]) return;
    this.weeklyState.progress[id] = next;
    if (next >= def.target) {
      this.completeWeekly(id, def);
    } else {
      saveWeeklyState(this.weeklyState);
      this.renderWeeklyRow(id, def);
    }
  }

  private completeWeekly(id: WeeklyChallengeId, def: WeeklyChallengeDef): void {
    if (this.weeklyState.completed[id]) return;
    this.weeklyState.completed[id] = true;
    this.weeklyState.progress[id] = def.target;
    saveWeeklyState(this.weeklyState);
    this.renderWeeklyRow(id, def);
    this.onWeeklyComplete?.({ id, title: def.title, xp: def.xp });
  }

  private renderWeeklyRow(id: WeeklyChallengeId, def: WeeklyChallengeDef): void {
    const els = this.weeklyRowEls.get(id);
    if (!els) return;
    const done = this.weeklyState.completed[id];
    const cur = this.weeklyState.progress[id];
    els.prog.textContent = done ? 'Complete' : `${Math.min(cur, def.target)} / ${def.target}`;
    els.prog.style.color = done ? '#b8a0ff' : '#a898e8';
    els.row.style.opacity = done ? '0.78' : '1';

    const check = els.row.firstElementChild as HTMLSpanElement;
    if (done) {
      check.textContent = '✓';
      check.style.background = 'rgba(155,123,255,0.28)';
      check.style.borderColor = 'rgba(196,181,253,0.75)';
      check.style.color = '#d4c4ff';
    } else {
      check.textContent = '';
      check.style.background = 'transparent';
      check.style.borderColor = 'rgba(155,123,255,0.55)';
      check.style.color = 'transparent';
    }
    if (this.collapsed) this.headerLabel.textContent = this.getCompactLabel();
  }

  private renderAllWeekly(): void {
    for (const c of this.weeklyDefs) {
      this.renderWeeklyRow(c.id, c);
    }
  }
}
